import { PropTypes } from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Tab, Tabs } from "react-bootstrap";
import PinnedActivities from "~/components/drawers/timeline/PinnedActivities";
import GlobalContainer from "~/components/global_container";
import useActivities from "~/hooks/lead/useActivities";
import usePinning from "~/hooks/lead/usePinning";
import dayjs from "~/lib/dayjs";
import CampaignSubscriptionStore from "~/stores/campaign_subscription_store";
import LeadDrawerStore from "~/stores/lead_drawer_store";
import TaskStore from "~/stores/task_store";
import Activities from "./timeline/Activities";

const tabs = [
  "all",
  "notes",
  "emails",
  "calls",
  "texts",
  "tasks",
  "appointments",
  "changes",
];

const LeadDrawerTimeline = ({
  lead = {},
  setCurrentFormWithData = () => {
    /* no-op */
  },
  activityId = null,
  defaultTab = null,
  refreshVersion = 0,
}) => {
  const currentUser = Rails?.helpers?.currentUser || {};
  const [activeTab, setActiveTab] = useState(
    defaultTab || GlobalContainer.urlParams()?.tab || "all",
  );
  const [hideOlderThan, setHideOlderThan] = useState(new Date());

  const {
    activities,
    setActivities,
    loadingActivities,
    loadingPrevActivities,
    nextPage,
    prevPage,
    loadMoreActivities,
    fetchActivities,
    pinnedActivities,
    setPinnedActivities,
    pinnedCount,
    setPinnedCount,
  } = useActivities(lead.id, activeTab, activityId);

  const previousRefresh = useRef({ leadId: lead.id, version: refreshVersion });
  useEffect(() => {
    const previous = previousRefresh.current;
    previousRefresh.current = { leadId: lead.id, version: refreshVersion };
    if (previous.leadId === lead.id && previous.version !== refreshVersion) {
      // Show the newest activity, even when opened from an older activity link.
      fetchActivities(true, false);
    }
  }, [lead.id, refreshVersion, fetchActivities]);

  const {
    pinAlert,
    setPinAlert,
    pinLoadingIds,
    handlePinActivity,
    handleUnpinActivity,
    buildPinProps,
  } = usePinning(lead.id, currentUser, {
    pinnedActivities,
    setPinnedActivities,
    pinnedCount,
    setPinnedCount,
    setActivities,
  });

  const attachStoreListener = useCallback(
    (store, actions, lastActionProperty) => {
      const listener = store.addListener(() => {
        const state = store.getState();

        if (actions.includes(state[lastActionProperty])) {
          fetchActivities(true, false);
        }
      });

      return () => listener.remove();
    },
    [fetchActivities],
  );

  const toggleActivityInternal = useCallback(
    (updatedActivity) => {
      setActivities((prev) =>
        prev.map((a) =>
          a.id === updatedActivity.id ? { ...a, ...updatedActivity } : a,
        ),
      );
      setPinnedActivities((prev) =>
        prev.map((a) =>
          a.id === updatedActivity.id ? { ...a, ...updatedActivity } : a,
        ),
      );
    },
    [setActivities, setPinnedActivities],
  );

  const removeActivityFromList = useCallback(
    (removeActivityId) => {
      setActivities((prevActivities) =>
        prevActivities.filter(
          (activity) => activity && activity.id !== removeActivityId,
        ),
      );
      // Also remove from pinned if present
      setPinnedActivities((prev) => {
        const updated = prev.filter((a) => a.id !== removeActivityId);
        if (updated.length < prev.length) {
          setPinnedCount((c) => Math.max(0, c - 1));
        }
        return updated;
      });
    },
    [setActivities, setPinnedActivities, setPinnedCount],
  );

  useEffect(
    () =>
      attachStoreListener(
        CampaignSubscriptionStore,
        ["createCampaignSubscriptionDone", "deleteCampaignSubscriptionDone"],
        "lastCampaignSubscriptionStoreAction",
      ),
    [lead.id, activeTab, attachStoreListener],
  );

  useEffect(
    () =>
      attachStoreListener(
        TaskStore,
        ["createTaskDone", "deleteTaskDone"],
        "lastTaskStoreAction",
      ),
    [lead.id, activeTab, attachStoreListener],
  );

  useEffect(
    () =>
      attachStoreListener(
        LeadDrawerStore,
        [
          "updateLeadDone",
          "updateLeadRoleDone",
          "createLeadAppointmentDone",
          "createLeadSmsMessageDone",
          "createLeadEmailDone",
          "createLeadActivityDone",
          "updateLeadActivityDone",
          "cancelScheduledLeadEmailDone",
          "updateLeadEmailDone",
          "updateLeadSmsMessageDone",
          "cancelScheduledLeadSmsMessageDone",
        ],
        "leadDrawerStoreAction",
      ),
    [lead.id, activeTab, attachStoreListener],
  );

  useEffect(() => {
    if (GlobalContainer.product() === "retention" && lead.joined_at) {
      const hideOlderThanDate = dayjs(lead.joined_at).add(1, "second").toDate();
      setHideOlderThan(hideOlderThanDate);
    }
  }, [lead.joined_at]);

  const handleTabSelect = useCallback(
    (key, e) => {
      if (activeTab === key) {
        return;
      }
      e.currentTarget.blur();
      setActivities([]);
      setActiveTab(key);
    },
    [activeTab, setActivities],
  );

  const productClass =
    GlobalContainer.product() === "retention"
      ? "retention-activities"
      : "recruiting-activities";

  const memoizedTabs = useMemo(
    () => (
      <Tabs
        id="activities-tabs"
        activeKey={activeTab}
        onSelect={handleTabSelect}
        variant="pills"
        className={productClass}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab}
            disabled={loadingActivities}
            eventKey={tab}
            title={_lodash.startCase(tab)}
          />
        ))}
      </Tabs>
    ),
    [activeTab, loadingActivities, handleTabSelect, productClass],
  );

  return (
    <>
      <h3 className="mb15">Activity</h3>

      {memoizedTabs}

      {pinAlert && (
        <Alert
          variant={pinAlert.variant}
          dismissible
          onClose={() => setPinAlert(null)}
          className="mt10"
        >
          {pinAlert.message}
        </Alert>
      )}

      <PinnedActivities
        pinnedActivities={pinnedActivities}
        pinnedCount={pinnedCount}
        activeTab={activeTab}
        lead={lead}
        currentUser={currentUser}
        onUnpin={handleUnpinActivity}
        setCurrentFormWithData={setCurrentFormWithData}
        toggleActivityInternal={toggleActivityInternal}
        removeActivity={removeActivityFromList}
        pinLoadingIds={pinLoadingIds}
      />

      <Activities
        loadingActivities={loadingActivities}
        loadingPrevActivities={loadingPrevActivities}
        activities={activities}
        activityId={activityId}
        activeTab={activeTab}
        nextPage={nextPage}
        prevPage={prevPage}
        loadMoreActivities={loadMoreActivities}
        hideOlderThan={hideOlderThan}
        lead={lead}
        setCurrentFormWithData={setCurrentFormWithData}
        toggleActivityInternal={toggleActivityInternal}
        removeActivityFromList={removeActivityFromList}
        buildPinProps={buildPinProps}
      />
    </>
  );
};

LeadDrawerTimeline.propTypes = {
  lead: PropTypes.shape({
    joined_at: PropTypes.string,
    id: PropTypes.number,
  }),
  setCurrentFormWithData: PropTypes.func,
  activityId: PropTypes.number,
  defaultTab: PropTypes.string,
  refreshVersion: PropTypes.number,
};

export default React.memo(LeadDrawerTimeline);
